import { inject, Injectable } from '@angular/core';
import { User } from '@angular/fire/auth';
import { ActivatedRouteSnapshot, CanActivate, GuardResult, MaybeAsync, Router, RouterStateSnapshot } from '@angular/router';
import { map, Observable, of, pipe, switchMap, take, UnaryFunction } from 'rxjs';

import { AuthCoreService } from './auth.service';

//#region Auth Guard Types
export type AuthPipe = UnaryFunction<Observable<User | null>, Observable<boolean | string | any[]>>;
export type AuthPipeGenerator = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => AuthPipe;

export type AuthRedirect = (redirect: string | any[]) => AuthPipe;
export type AuthClaimsRedirect = (redirect: string | any[], ...claims: string[]) => AuthPipe;
//#endregion

@Injectable({
  providedIn: 'any'
})
export class AuthGuard implements CanActivate {

  //#region Services
  #auth: AuthCoreService = inject(AuthCoreService);
  #router: Router = inject(Router);
  //#endregion

  //#region Guard Methods
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): MaybeAsync<GuardResult> {
    const authPipeFactory: AuthPipeGenerator = route.data['authGuardPipe'] as AuthPipeGenerator || ((route, state) => isSigned);
    return this.#auth.authState$.pipe(take(1), authPipeFactory(route, state), map((result: boolean | string | any[]) => {
      // Convert string or array result to UrlTree and return.
      if (typeof result === 'string')
        return this.#router.parseUrl(result);
      else if (Array.isArray(result))
        return this.#router.createUrlTree(result);

      // Return boolean result directly.
      return result;
    }));
  }
  //#endregion
}

/**
 * Returns an AuthGuard canActivate object with the given AuthPipeGenerator function.
 * 
 * @param pipe - AuthPipeGenerator function.
 * @returns - AuthGuard canActivate object with the given AuthPipeGenerator function.
 */
export const canActivate = (pipe: AuthPipeGenerator) => ({
  canActivate: [AuthGuard], data: { authGuardPipe: pipe }
});

//#region Auth Pipes
export const idTokenResult = switchMap((user: User | null) => user ? user.getIdTokenResult() : of(null));
//#endregion

//#region User Pipes
export const isSigned: AuthPipe = map(user => !!user);

export const isEmailVerified: AuthPipe = map((user: User | null) => !!user && user.emailVerified);
export const isNotAnonymous: AuthPipe = map((user: User | null) => !!user && !user.isAnonymous);
//#endregion

//#region User Claim Pipes
export const customClaims = pipe(idTokenResult, map(idTokenResult => idTokenResult ? idTokenResult.claims : []));
export const anyCustomClaim: (...claims: string[]) => AuthPipe = (...claims: string[]) => pipe(customClaims, map(customClaim => claims.some((claim: string) => customClaim.hasOwnProperty(claim))));
export const hasCustomClaim: (claim: string) => AuthPipe = (claim) => pipe(customClaims, map(claims => claims.hasOwnProperty(claim)));
//#endregion

//#region Redirect Pipes
export const redirectSignedTo: AuthRedirect = (redirect) => pipe(isSigned, map(isSigned => (isSigned && redirect) || true));
export const redirectUnsignedTo: AuthRedirect = (redirect) => pipe(isSigned, map(isSigned => (isSigned || redirect)));

export const redirectAnyClaimsTo: AuthClaimsRedirect = (redirect, ...claims) => pipe(anyCustomClaim(...claims), map(hasClaim => (hasClaim && redirect) || true));
export const redirectNotClaimsTo: AuthClaimsRedirect = (redirect, ...claims) => pipe(anyCustomClaim(...claims), map(hasClaim => (hasClaim || redirect)));

export const redirectAuthorizedTo: AuthRedirect = (redirect) => pipe(isNotAnonymous, map(isAuthorized => (isAuthorized && redirect) || true));
export const redirectUnauthorizedTo: AuthRedirect = (redirect) => pipe(isNotAnonymous, map(isAuthorized => (isAuthorized || redirect)));
//#endregion